import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { integrationService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Boxes, Loader2, Plug, ArrowRight } from "lucide-react";
import { ConnectorIcon, useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, Badge, STATUS_STYLE, fmtDate } from "./shared";

export default function IntegrationsListPage() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setItems([]); return; }
    setLoading(true);
    integrationService.list(params)
      .then((d) => setItems(d.items || []))
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, params]);
  useEffect(() => { load(); }, [load]);

  const suffix = tenantId ? `?tenantId=${tenantId}` : "";

  return (
    <div data-testid="integrations-list-page">
      <PageHeader icon={Boxes} title="Integrations" subtitle="Your tenant's configured connectors.">
        <TenantPicker {...scope} />
        <Link to="/admin/integrations/connectors" className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-primary/90" data-testid="add-from-catalog">
          <Plug className="h-4 w-4" /> Add
        </Link>
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="integrations-empty">
          <Plug className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No integrations yet. Add one from the Connectors catalog.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="integrations-list">
          {items.map((i) => (
            <Link key={i.id} to={`/admin/integrations/${i.id}${suffix}`}
              className="flex items-center gap-3 bg-card border border-border rounded-xl p-4 hover:border-primary/40 transition-colors" data-testid={`integration-row-${i.id}`}>
              <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><ConnectorIcon code={i.connectorCode} className="h-5 w-5" /></span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground truncate">{i.name}</p>
                <p className="text-[11px] text-muted-foreground">{i.connector?.name} · {i.direction} · last sync {fmtDate(i.lastSyncAt)}</p>
              </div>
              <Badge label={i.status} styleMap={STATUS_STYLE} />
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
